/**
 * Authentication guard
 *
 * Verifies the user session before every navigation:
 * - lets unguarded routes through untouched
 * - re-validates the Cognito session for authenticated users
 * - recovers from inconsistent auth state (flag set, no profile) via sign-out
 * - handles MFA verification/setup redirects for partially authenticated users
 * - preserves the intended route across login redirects
 *
 * NOTE: logoutService is imported dynamically inside the guard to avoid
 * circular deps (router → logoutService → api.ts → router).
 */

import type { NavigationGuard, RouteRecordName } from 'vue-router';

import { getCurrentUser, signOut } from 'aws-amplify/auth';

import { useUserProfile } from '@/composables/useUserProfile';
import { logAppError } from '@/config/sentryConfig';
import { authHelpers } from '@/services/auth/auth-helper';
import { getSessionValidity } from '@/services/auth/tokenRefresh';
import useAuthStore from '@/stores/auth';
import { resetAllStores } from '@/utils/resetStores';
import { AUTH_PAGE_NAMES } from '../authPages';
import {
  clearIntendedRouteRestoredMark,
  getAndClearIntendedRoute,
  markIntendedRouteRestored,
  saveIntendedRoute,
  wasIntendedRouteRestored
} from '../intendedRoute';
import { isUnguardedRoute, isUserAuthenticated } from './helpers';

const isAuthPage = (name: RouteRecordName | null | undefined): boolean =>
  typeof name === 'string' && AUTH_PAGE_NAMES.includes(name);

const authGuard: NavigationGuard = async (to, from, next) => {
  // A restored intended route keeps its "restored" mark only until the user
  // navigates somewhere else; clear it on any other destination so a genuine
  // later auth failure on that route is saved for restoration normally.
  if (!wasIntendedRouteRestored(to)) {
    clearIntendedRouteRestoredMark();
  }

  // Skip authentication checks for unguarded routes
  if (isUnguardedRoute(to)) {
    // Authenticated users skip the auth pages (login + password flows) — send
    // them to landing, which routes on to their actual home.
    if (isUserAuthenticated() && isAuthPage(to.name)) {
      return next({ name: 'landing' });
    }
    return next();
  }

  // ...and continue if user is authenticated
  if (isUserAuthenticated()) {
    const { profile } = useUserProfile();

    // Inconsistent state: auth flag is true but profile never loaded.
    // Force a clean sign-out so the user gets a fresh session.
    if (!profile.value) {
      logAppError({
        error: new Error(
          'Inconsistent auth state: authenticated but no user profile loaded'
        ),
        functionName: 'authGuard',
        section: 'router',
        level: 'warning'
      });

      try {
        await signOut();
      } catch (error) {
        logAppError({
          error,
          functionName: 'authGuard',
          section: 'signOut_cleanup',
          level: 'warning',
          message: 'Failed to sign out during inconsistent auth state recovery'
        });
      }
      resetAllStores();
      sessionStorage.clear();
      saveIntendedRoute(to);
      return next({ name: 'login' });
    }

    // Re-validate the Cognito session here: the Pinia auth flag only resets on a
    // full reload, so a token that expired mid-session is otherwise never caught
    // on client-side navigation. `to` is preserved for restoration after re-login.
    const sessionValidity = await getSessionValidity();

    if (sessionValidity === 'invalid') {
      const { logout, LogoutReason } =
        await import('@/services/auth/logoutService');
      // Abort the current router navigation before triggering a hard redirect.
      // This avoids vue-router v4 warnings about `next` not being called.
      next(false);
      // Don't re-save a just-restored route that is now failing an auth check —
      // that would trap the user in a logout loop (see markIntendedRouteRestored).
      const routeToRestore = wasIntendedRouteRestored(to) ? undefined : to;
      await logout(undefined, LogoutReason.SessionExpired, routeToRestore);
      return; // Stop guard execution after logout (hard redirect handles navigation)
    }

    if (to.name === 'landing') {
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute) {
        markIntendedRouteRestored(intendedRoute.path);
        return next(intendedRoute);
      }
    }
    return next();
  }

  // Try to get current authenticated user from Cognito
  try {
    const currentUser = await getCurrentUser();

    // Check if user needs MFA verification or setup
    if (
      authHelpers.needsMFAVerification(currentUser) ||
      authHelpers.needsMFASetup(currentUser)
    ) {
      // If trying to access a protected route but MFA is required, redirect to login
      if (to.name !== 'login') {
        saveIntendedRoute(to);
        return next({ name: 'login' });
      }

      // Allow access to login page to handle MFA
      return next();
    }

    // User is authenticated but not reflected in store yet
    const authStore = useAuthStore();
    authStore.setUserAuthenticated(true);

    // Check for intended route after authentication
    if (to.name === 'landing') {
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute) {
        markIntendedRouteRestored(intendedRoute.path);
        return next(intendedRoute);
      }
    }

    return next();
  } catch {
    // User is not authenticated, redirect to login unless already going there
    if (to.name === 'login') {
      return next();
    }

    // Save the intended destination before redirecting to login
    saveIntendedRoute(to);
    return next({ name: 'login' });
  }
};

export default authGuard;
